import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { type UseFormSetValue } from 'react-hook-form';

import { SimpleButton, SimpleSingleSelect, Loading, ErrorMessage, SimpleTextField } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { getZeroOption, type Option, Constants } from '@/src/utils';
import type { InitialOptions, FilterState, StandardOption } from '../models/filterDTOs';
import QuestionTable from './QuestionTable';
import type { StimulusReferenceQuestionDTO } from '../../../models/questionDTOs';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

interface QuestionSelectorProps {
    setValue: UseFormSetValue<any>;
    selectedQuestions: StimulusReferenceQuestionDTO[];
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const NoStandardOption = getZeroOption('No standards found');

// Very similar implementation to ViewQuestionsFilters
const QuestionSelector: React.FC<QuestionSelectorProps> = ({ setValue, selectedQuestions }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [filterState, setFilterState] = useState<FilterState>({
        gradeLevelMasterId: 0,
        standardId: 0,
    });

    const [gradeLevelMasterOptions, setgradeLevelMasterOptions] = useState<Option[]>([
        LoadingOption,
    ]);
    const [standardData, setStandardData] = useState<StandardOption[]>([]);
    const [standardOptions, setStandardOptions] = useState<Option[]>([LoadingOption]);
    const [questionData, setQuestionData] = useState<StimulusReferenceQuestionDTO[]>([]);

    const [fieldsChanged, setFieldsChanged] = useState(false);
    const [searchString, setSearchString] = useState('');

    const availableQuestions = questionData
        .filter((q) => q.title.toLowerCase().trim().includes(searchString.toLowerCase().trim()))
        .filter((q) => !selectedQuestions.some((sel) => sel.id === q.id));

    // Fetch grade options on mount
    useEffect(() => {
        (async function fetchOptions() {
            try {
                setLoading(true);
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getStandard=true'
                );
                const data = res.data;

                const newGradeLevelMasterOptions = data.gradeLevelMasterOptions;
                setgradeLevelMasterOptions([AllOption, ...newGradeLevelMasterOptions]);
                setFilterState((prev) => ({ ...prev, gradeLevelMasterId: Number(AllOption.id) }));

                const newStandardOptions = data.standardOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setStandardOptions([AllOption, ...newStandardOptions]);
                setFilterState((prev) => ({ ...prev, standardId: Number(AllOption.id) }));
                setStandardData(newStandardOptions);
            } catch {
                setError('Failed to load grade options');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const updateStandardOptions = useCallback(
        (data: Array<StandardOption>, gradeLevelMasterId: number) => {
            // Reset filter if needed
            if (gradeLevelMasterId === AllOption.id) {
                setStandardOptions([AllOption]);
                setFilterState((prev) => ({ ...prev, standardId: Number(AllOption.id) }));
                return;
            }

            const newStandardOptions = data
                .filter((option) => {
                    return option.gradeLevelMasterId === gradeLevelMasterId;
                })
                .map((option) => ({
                    id: option.id,
                    name: option.name,
                }));

            if (!newStandardOptions.length) {
                setStandardOptions([NoStandardOption]);
                setFilterState((prev) => ({ ...prev, standardId: Number(NoStandardOption.id) }));
                return;
            }

            setStandardOptions([AllOption, ...newStandardOptions]);
            setFilterState((prev) => ({ ...prev, standardId: Number(AllOption.id) }));
        },
        []
    );

    // Fetch questions when grade changes
    const refreshTableData = useCallback(
        async ({ gradeLevelMasterId, standardId }: FilterState = filterState) => {
            setError('');
            setLoading(true);

            setSearchString('');

            let requestUrl = `assessments/questions`;
            requestUrl += `?gradeLevelMasterIds=${gradeLevelMasterId}`;
            requestUrl += `&standardId=${standardId}`;

            try {
                const res = await apiRequest<StimulusReferenceQuestionDTO[]>('GET', requestUrl);
                setQuestionData(res.data.filter((q) => q.questionTypeMasterId !== qtm.STIMULUS));
            } catch {
                setError('Failed to fetch question data');
            } finally {
                setLoading(false);
            }
        },
        [filterState]
    );

    const handleQuestionAction = useCallback(
        (
            row: StimulusReferenceQuestionDTO,
            actionType: 'add' | 'remove' | 'moveUp' | 'moveDown'
        ) => {
            if (actionType === 'add') {
                const newSelected = [...selectedQuestions, row];
                setValue('selectedQuestions', newSelected);
            } else if (actionType === 'remove') {
                const index = selectedQuestions.findIndex((q) => q.id === row.id);
                const newSelected = selectedQuestions.filter((q) => q.id !== row.id);
                if (index !== -1) {
                    setValue('selectedQuestions', newSelected);
                }
            } else if (actionType === 'moveUp') {
                const index = selectedQuestions.findIndex((q) => q.id === row.id);
                if (index > 0) {
                    const newSelected = [...selectedQuestions];
                    const selectedVal = newSelected[index];
                    const swapWithVal = newSelected[index - 1];
                    newSelected[index] = swapWithVal;
                    newSelected[index - 1] = selectedVal;

                    setValue('selectedQuestions', newSelected);
                }
            } else if (actionType === 'moveDown') {
                const index = selectedQuestions.findIndex((q) => q.id === row.id);
                if (index !== -1 && index < selectedQuestions.length - 1) {
                    const newSelected = [...selectedQuestions];

                    const selectedVal = newSelected[index];
                    const swapWithVal = newSelected[index + 1];
                    newSelected[index] = swapWithVal;
                    newSelected[index + 1] = selectedVal;

                    setValue('selectedQuestions', newSelected);
                }
            }
        },
        [setValue, selectedQuestions]
    );

    return (
        <div className="border rounded p-4 mb-4">
            <h3 className="font-semibold mb-2">Select Questions</h3>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                    mb: 2,
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 1 }}>
                    <SimpleSingleSelect
                        name="grade-level-select"
                        label="Grade Level"
                        value={filterState.gradeLevelMasterId}
                        onChange={(e) => {
                            setFieldsChanged(true);
                            const newGradeLevel = Number(e.target.value);
                            setFilterState((prev) => ({
                                ...prev,
                                gradeLevelMasterId: newGradeLevel,
                            }));
                            updateStandardOptions(standardData, newGradeLevel);
                        }}
                        options={gradeLevelMasterOptions}
                        sx={{ minWidth: 140, maxWidth: 280 }}
                    />
                    <SimpleSingleSelect
                        name="standard-select"
                        label="Standard"
                        value={filterState.standardId}
                        onChange={(e) => {
                            setFieldsChanged(true);
                            setFilterState((prev) => ({
                                ...prev,
                                standardId: Number(e.target.value),
                            }));
                        }}
                        options={standardOptions}
                        sx={{ minWidth: 140, maxWidth: 280 }}
                    />
                    <SimpleButton
                        onClick={() => {
                            setFieldsChanged(false);
                            refreshTableData(filterState);
                        }}
                        variant={fieldsChanged ? 'outlined' : 'contained'}
                        color="primary"
                    >
                        Search
                    </SimpleButton>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'right', mb: 1 }}>
                    <Box sx={{ minWidth: 240, maxWidth: 320 }}>
                        <SimpleTextField
                            label="Search questions"
                            variant="outlined"
                            name="searchString"
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                        />
                        <ErrorMessage error={error} style={{ marginTop: 4 }} />
                    </Box>
                </Box>
            </Box>
            <Loading visible={loading} message="Loading questions..." />
            <ErrorMessage error={error} />

            <div className="mt-4" />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                    mb: 2,
                }}
            >
                <div>
                    <h2>Available Questions</h2>
                    <QuestionTable
                        data={availableQuestions}
                        onAction={handleQuestionAction}
                        actionType="add"
                        allowReorder={false}
                    />
                </div>
                <div>
                    <h2>Selected Questions</h2>
                    <QuestionTable
                        data={selectedQuestions}
                        onAction={handleQuestionAction}
                        actionType="remove"
                        allowReorder={true}
                    />
                </div>
            </Box>
        </div>
    );
};

export default QuestionSelector;
